<template>
  <div class="sales-volume-configure-panel">
    <div class="sales-volume-configure-header">
      <div>
        <div class="sales-volume-configure-title">Sales Volume Alert Thresholds</div>
        <div class="sales-volume-configure-subtitle">
          Listings and saved thresholds come from the API. Changes apply to all future alerts for that product.
        </div>
        <div
          v-if="listTotal > listRows.length"
          class="sales-volume-configure-cap-note"
        >
          Showing {{ listRows.length }} of {{ listTotal }} listings (page {{ listPage }}).
          Use Download CSV to edit the rest in bulk.
        </div>
      </div>
      <button type="button" class="sales-volume-configure-close" aria-label="Close" @click="$emit('close')">
        <i class="close icon"></i>
      </button>
    </div>

    <div v-if="thresholdsLoadError" class="sales-volume-thresholds-load-error" role="alert">
      {{ thresholdsLoadError }}
    </div>

    <div class="sales-volume-subtype-tabs">
      <button
        v-for="tab in subtypeTabs"
        :key="tab.id"
        type="button"
        :class="['sales-volume-subtype-tab', { active: salesSubtype === tab.id }]"
        @click="salesSubtype = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="sales-volume-pane-body">
      <p v-if="salesSubtype === 'hours'" class="sales-volume-pane-desc">
        Alert when a product has had no sales for this many consecutive hours. Default: 72 hrs.
      </p>
      <p v-else-if="salesSubtype === 'fixed'" class="sales-volume-pane-desc">
        Set absolute daily sales floor/ceiling. Alert fires when sales drop below or spike above these values.
      </p>
      <p v-else-if="salesSubtype === 'rolling'" class="sales-volume-pane-desc">
        Alert when daily sales deviate from the rolling average by a percentage.
      </p>
      <p v-else-if="salesSubtype === 'bsr'" class="sales-volume-pane-desc">
        Alert when Best Seller Rank spikes above this number (higher rank = fewer sales).
      </p>

      <div class="sales-volume-table-wrap">
        <table class="sales-volume-settings-table">
          <thead>
            <tr>
              <th class="col-product">Listing</th>
              <th class="col-mkt">Mkt</th>
              <th v-for="col in activeColumns" :key="col.key">{{ col.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in listRows" :key="rowKey(row)">
              <td>
                <div class="sales-volume-asin-info">
                  <span class="sales-volume-asin-name">{{ row.asin || '—' }}</span>
                  <span class="sales-volume-asin-code">ID {{ row.productId }}</span>
                </div>
              </td>
              <td>
                <span class="sales-volume-mkt">
                  <i :class="marketplaceFlagClass(row.marketplace) + ' flag'"></i>
                  {{ marketplaceCode(row.marketplace) }}
                </span>
              </td>
              <template v-if="salesSubtype === 'hours'">
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].noSalesHours"
                      type="number"
                      min="1"
                      class="sales-volume-threshold-input"
                      placeholder="72 (default)"
                    />
                    <span class="sales-volume-threshold-unit">hours</span>
                  </div>
                </td>
              </template>
              <template v-else-if="salesSubtype === 'fixed'">
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].dailyFloor"
                      type="number"
                      min="0"
                      class="sales-volume-threshold-input"
                      placeholder="—"
                    />
                    <span class="sales-volume-threshold-unit">units/day</span>
                  </div>
                </td>
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].dailyCeiling"
                      type="number"
                      min="0"
                      class="sales-volume-threshold-input"
                      placeholder="—"
                    />
                    <span class="sales-volume-threshold-unit">units/day</span>
                  </div>
                </td>
              </template>
              <template v-else-if="salesSubtype === 'rolling'">
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].rollingDrop"
                      type="number"
                      min="1"
                      max="100"
                      class="sales-volume-threshold-input"
                      placeholder="30 (default)"
                    />
                    <span class="sales-volume-threshold-unit">%</span>
                  </div>
                </td>
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].rollingSpike"
                      type="number"
                      min="1"
                      max="100"
                      class="sales-volume-threshold-input"
                      placeholder="—"
                    />
                    <span class="sales-volume-threshold-unit">%</span>
                  </div>
                </td>
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].rollingDays"
                      type="number"
                      min="1"
                      class="sales-volume-threshold-input"
                      placeholder="14 (default)"
                    />
                    <span class="sales-volume-threshold-unit">days</span>
                  </div>
                </td>
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].rollingMinUnits"
                      type="number"
                      min="0"
                      class="sales-volume-threshold-input"
                      placeholder="3 (default)"
                    />
                    <span class="sales-volume-threshold-unit">units</span>
                  </div>
                </td>
              </template>
              <template v-else-if="salesSubtype === 'bsr'">
                <td>
                  <div class="sales-volume-input-wrap">
                    <input
                      v-model.number="forms[rowKey(row)].bsrAbove"
                      type="number"
                      min="1"
                      class="sales-volume-threshold-input"
                      placeholder="not set"
                    />
                  </div>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="sales-volume-csv-strip">
      <div class="sales-volume-csv-label">
        <i class="info circle icon"></i>
        Large catalog?
        <strong>Download → edit in bulk → re-upload</strong>
      </div>
      <div class="sales-volume-csv-actions">
        <button type="button" class="sales-volume-btn-csv sales-volume-btn-csv-dl" @click="downloadCsv">
          <i class="download icon"></i>
          Download CSV
        </button>
        <span class="sales-volume-csv-divider" />
        <label class="sales-volume-btn-csv sales-volume-btn-csv-ul">
          <i class="upload icon"></i>
          Upload CSV
          <input type="file" accept=".csv" class="sales-volume-csv-file" @change="importCsv" />
        </label>
      </div>
    </div>

    <div class="sales-volume-panel-footer">
      <span class="sales-volume-footer-note">Changes apply to future alerts only — existing alerts are unaffected.</span>
      <div class="sales-volume-footer-actions">
        <button type="button" class="sales-volume-btn-cancel" @click="$emit('close')">Cancel</button>
        <button type="button" class="sales-volume-btn-save" :disabled="saving || listRows.length === 0" @click="saveAll">
          <i v-if="saving" class="notched circle loading icon"></i>
          Save Changes
        </button>
      </div>
    </div>

    <div v-if="saveError" class="sales-volume-panel-error">{{ saveError }}</div>
  </div>
</template>

<script>
import {
  extractSalesVolumeListItems,
  productIdFromSalesVolumeRow,
  salesVolumeHttpResponseEntity,
  parseJsonIfNeeded,
  salesVolumeFormToApiSettings,
  formFieldsFromSalesVolumeSettings
} from '../../utils/salesVolumeAlertThresholdsApiParse.js'

function unwrapListResponseEntity (res) {
  if (Array.isArray(res)) return res
  var e = salesVolumeHttpResponseEntity(res)
  e = parseJsonIfNeeded(e)
  if (e != null) return e
  if (res && typeof res === 'object' && !Array.isArray(res) && res.data != null) {
    var d = parseJsonIfNeeded(res.data)
    if (d != null && typeof d === 'object') return d
  }
  return null
}

function bodyObjectFromResponse (rawBody) {
  var b = parseJsonIfNeeded(rawBody)
  if (b == null) b = rawBody
  if (b != null && typeof b === 'object' && !Array.isArray(b)) return b
  return {}
}

function parseSettingsRaw (raw) {
  if (raw == null) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch (e) {
      return {}
    }
  }
  return typeof raw === 'object' ? raw : {}
}

function normalizeApiListingRow (apiRow) {
  if (!apiRow || typeof apiRow !== 'object') return null
  var pid = productIdFromSalesVolumeRow(apiRow)
  if (!pid) return null
  return {
    productId: String(pid),
    asin: apiRow.asin != null ? String(apiRow.asin) : '',
    marketplace: apiRow.marketplace != null ? String(apiRow.marketplace) : '',
    sellerId: apiRow.seller_id != null ? String(apiRow.seller_id) : (apiRow.sellerId != null ? String(apiRow.sellerId) : '')
  }
}

export default {
  name: 'SalesVolumeThresholdsPanel',
  props: {
    /** When true, panel is visible — triggers GET list (no product_ids; same idea as Stock thresholds panel). */
    panelOpen: { type: Boolean, default: false },
    /** Base URL for sales volume thresholds (GET, PATCH, CSV). */
    thresholdsApiBase: { type: String, default: '/api/v1/sales-volume-alert-thresholds' },
    /** Optional: scope PATCH / CSV when Issues navbar has one seller selected. */
    sellerAccountId: { type: String, default: null },
    /** Paginated list GET (backend defaults apply if unsupported). */
    listPage: { type: Number, default: 0 },
    listPageSize: { type: Number, default: 100 }
  },
  data: function () {
    return {
      /** Rows from GET /sales-volume-alert-thresholds `items`. */
      listRows: [],
      listTotal: 0,
      /** productId → last fetched settings object (merge after save / reload). */
      rowApiSettings: {},
      salesSubtype: 'hours',
      forms: {},
      saving: false,
      saveError: null,
      thresholdsLoading: false,
      thresholdsLoadError: null,
      /** In-flight GET generation; must not use `_` prefix — Vue 2 does not proxy `_`/`$` data keys to `this`. */
      thresholdsFetchSeq: 0,
      subtypeTabs: [
        { id: 'hours', label: 'Hours without Sales' },
        { id: 'fixed', label: 'Daily Fixed Limits' },
        { id: 'rolling', label: 'Rolling Average' },
        { id: 'bsr', label: 'BSR Limits' }
      ]
    }
  },
  computed: {
    activeColumns: function () {
      if (this.salesSubtype === 'hours') {
        return [{ key: 'h', label: 'Alert if no sales in last' }]
      }
      if (this.salesSubtype === 'fixed') {
        return [
          { key: 'f1', label: 'Alert if drops below' },
          { key: 'f2', label: 'Alert if spikes above' }
        ]
      }
      if (this.salesSubtype === 'rolling') {
        return [
          { key: 'r1', label: 'Drop alert (%)' },
          { key: 'r2', label: 'Spike alert (%)' },
          { key: 'r3', label: 'Avg over (days)' },
          { key: 'r4', label: 'Min units' }
        ]
      }
      return [{ key: 'b', label: 'Alert if BSR goes above' }]
    }
  },
  watch: {
    panelOpen: function (open) {
      if (!open) return
      this.reloadThresholdsFromApi()
    },
    listPage: function () {
      if (this.panelOpen) this.reloadThresholdsFromApi()
    },
    listPageSize: function () {
      if (this.panelOpen) this.reloadThresholdsFromApi()
    }
  },
  /**
   * Parent uses v-if when opening the panel, so this component mounts with panelOpen already true.
   * A watcher alone does not run on that first value change — only mounted + $nextTick reliably fires the GET.
   */
  mounted: function () {
    var self = this
    this.$nextTick(function () {
      if (self.panelOpen) {
        self.reloadThresholdsFromApi()
      }
    })
  },
  methods: {
    /** GET query: pagination + cache buster only (no product_ids). */
    listGetParams: function () {
      return {
        page: this.listPage,
        page_size: this.listPageSize
      }
    },
    listGetRequestOptions: function (extraParams) {
      return {
        params: Object.assign({}, this.listGetParams(), { _skcb: Date.now() }, extraParams || {}),
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        timeout: 45000
      }
    },
    httpParams: function () {
      var p = {}
      if (this.sellerAccountId) p.seller_account_id = this.sellerAccountId
      return p
    },
    reloadThresholdsFromApi: function () {
      return this.loadThresholdsFromApi()
    },
    rowKey: function (row) {
      return row && row.productId ? String(row.productId) : 'unknown'
    },
    marketplaceCode: function (mp) {
      if (!mp) return '—'
      var m = String(mp).toUpperCase()
      return m === 'GB' ? 'UK' : m
    },
    marketplaceFlagClass: function (mp) {
      if (!mp) return 'flag'
      var m = String(mp).toLowerCase()
      return (m === 'uk' ? 'gb' : m).replace('uk', 'gb')
    },
    buildPayload: function (form) {
      return { settings: salesVolumeFormToApiSettings(form) }
    },
    loadThresholdsFromApi: function () {
      var self = this
      /** Always emit so the parent prefetch bar clears (even on error or superseded fetch). */
      var finish = function () {
        self.$emit('thresholds-load-complete')
      }
      if (!this.panelOpen) {
        finish()
        return Promise.resolve()
      }
      if (!this.$http || typeof this.$http.get !== 'function') {
        this.thresholdsLoadError = 'HTTP client is not available.'
        finish()
        return Promise.resolve()
      }
      this.thresholdsFetchSeq += 1
      var seq = this.thresholdsFetchSeq
      this.thresholdsLoading = true
      this.thresholdsLoadError = null
      var base = this.thresholdsApiBase

      var applyPayload = function (res) {
        if (seq !== self.thresholdsFetchSeq) return
        var entity = unwrapListResponseEntity(res)
        var bodyObj = bodyObjectFromResponse(entity)
        var items = extractSalesVolumeListItems(entity)
        if (items.length === 0 && entity && Array.isArray(entity.items)) {
          items = entity.items
        }
        var total = bodyObj.total != null ? Number(bodyObj.total) : items.length
        if (isNaN(total)) total = items.length
        var nextRows = []
        var nextForms = {}
        var nextApi = {}
        var j
        for (j = 0; j < items.length; j++) {
          var apiRow = items[j]
          var lr = normalizeApiListingRow(apiRow)
          if (!lr) continue
          var raw = parseSettingsRaw(apiRow.settings)
          nextRows.push(lr)
          var k = self.rowKey(lr)
          nextForms[k] = formFieldsFromSalesVolumeSettings(raw)
          nextApi[lr.productId] = raw && typeof raw === 'object' ? Object.assign({}, raw) : {}
        }
        self.listRows = nextRows
        self.listTotal = total
        self.forms = nextForms
        self.rowApiSettings = nextApi
      }

      var run = function (extra) {
        return self.$http.get(base, self.listGetRequestOptions(extra)).then(function (res) {
          applyPayload(res)
        })
      }

      return run(null)
        .catch(function (res) {
          if (res && res.status === 304) {
            return run({ _skretry: Date.now() })
          }
          if (seq === self.thresholdsFetchSeq) {
            self.thresholdsLoadError = (res.body && res.body.message) || res.statusText || 'Could not load thresholds'
          }
          return Promise.resolve()
        })
        .then(function () {
          if (seq === self._fetchSeq) {
            self.thresholdsLoading = false
          }
          finish()
        })
    },
    mergeSavedIntoRowApiSettings: function (productId, settings) {
      var pid = String(productId)
      var prev = this.rowApiSettings[pid] && typeof this.rowApiSettings[pid] === 'object'
        ? Object.assign({}, this.rowApiSettings[pid])
        : {}
      var keys = Object.keys(settings || {})
      var i
      for (i = 0; i < keys.length; i++) {
        var key = keys[i]
        if (!Object.prototype.hasOwnProperty.call(settings, key)) continue
        var val = settings[key]
        if (val === null || val === undefined) delete prev[key]
        else prev[key] = val
      }
      this.$set(this.rowApiSettings, pid, prev)
    },
    saveAll: function () {
      var self = this
      if (this.listRows.length === 0) return
      this.saving = true
      this.saveError = null
      var updates = []
      var i
      for (i = 0; i < this.listRows.length; i++) {
        var row = this.listRows[i]
        var k = this.rowKey(row)
        var form = this.forms[k]
        if (!row.productId || !form) continue
        updates.push({
          product_id: String(row.productId),
          settings: salesVolumeFormToApiSettings(form)
        })
      }
      if (updates.length === 0) {
        this.saving = false
        this.saveError = 'No listing id for these rows — cannot save.'
        return
      }
      this.$http.patch(this.thresholdsApiBase, { updates: updates }, { params: this.httpParams(), timeout: 60000 }).then(function (res) {
        var body = res.body || {}
        var results = body.results || []
        var failed = body.failed != null ? Number(body.failed) : results.filter(function (x) { return x && x.ok === false }).length
        if (failed > 0) {
          var firstErr = results.filter(function (x) { return x && x.ok === false })[0]
          self.saveError = (firstErr && firstErr.error) ? String(firstErr.error) : ('Some rows failed to save (' + failed + ')')
          self.saving = false
          return
        }
        for (i = 0; i < updates.length; i++) {
          self.mergeSavedIntoRowApiSettings(updates[i].product_id, updates[i].settings)
        }
        self.saving = false
        self.$emit('saved')
      }, function (err) {
        self.saving = false
        self.saveError = (err.body && err.body.message) || err.statusText || 'Failed to save thresholds'
      })
    },
    downloadCsv: function () {
      var self = this
      var qs = []
      if (this.sellerAccountId) qs.push('seller_account_id=' + encodeURIComponent(this.sellerAccountId))
      var qstr = qs.length ? '?' + qs.join('&') : ''
      var exportUrl = this.thresholdsApiBase + '/export.csv' + qstr
      var defaultName = 'sales-volume-thresholds-' + new Date().toISOString().slice(0, 10) + '.csv'
      var xhr = new XMLHttpRequest()
      xhr.open('GET', exportUrl, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        if (xhr.status === 200) {
          var blob = xhr.response
          var url = window.URL.createObjectURL(blob)
          var link = document.createElement('a')
          link.href = url
          var cd = xhr.getResponseHeader('Content-Disposition')
          var filename = defaultName
          if (cd) {
            var m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (m && m[1]) filename = m[1].replace(/['"]/g, '').trim() || defaultName
          }
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } else {
          self.saveError = 'Could not download CSV.'
        }
      }
      xhr.onerror = function () {
        self.saveError = 'CSV download failed.'
      }
      xhr.send()
    },
    isCsvFile: function (file) {
      if (!file || !file.name) return false
      if (!/\.csv$/i.test(file.name)) return false
      if (!file.type) return true
      return file.type === 'text/csv' ||
        file.type === 'application/csv' ||
        file.type === 'text/plain' ||
        file.type === 'application/octet-stream'
    },
    importCsv: function (e) {
      var input = e.target
      if (!input.files || !input.files[0]) return
      var file = input.files[0]
      if (!this.isCsvFile(file)) {
        this.saveError = 'Please choose a CSV file (.csv).'
        input.value = ''
        return
      }
      var formData = new window.FormData()
      formData.append('file', file, file.name || 'sales-volume-thresholds-import.csv')
      var self = this
      var q = []
      if (this.sellerAccountId) q.push('seller_account_id=' + encodeURIComponent(this.sellerAccountId))
      var qimp = q.length ? '?' + q.join('&') : ''
      this.$http.post(this.thresholdsApiBase + '/import.csv' + qimp, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(function () {
        if (self.panelOpen) self.reloadThresholdsFromApi()
        self.$emit('saved')
        input.value = ''
      }, function (err) {
        self.saveError = (err.body && err.body.message) || 'CSV import failed'
        input.value = ''
      })
    }
  }
}
</script>

<style scoped>
.sales-volume-configure-panel {
  margin: 0 16px 12px;
  background: var(--bg-white);
  border: 1.5px solid var(--primary-navy-dark, #143a66);
  border-radius: 12px;
  overflow: hidden;
}

.sales-volume-configure-header {
  padding: 14px 20px;
  background: #eff4fb;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.sales-volume-thresholds-load-error {
  padding: 10px 20px;
  font-size: 13px;
  color: var(--status-critical);
  background: #fff8f6;
  border-bottom: 1px solid var(--border-light);
}

.sales-volume-configure-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--primary-navy-dark, #143a66);
}

.sales-volume-configure-subtitle {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.sales-volume-configure-cap-note {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 8px;
  max-width: 520px;
  line-height: 1.4;
}

.sales-volume-configure-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-tertiary);
  padding: 4px;
  line-height: 1;
}

.sales-volume-configure-close:hover {
  color: var(--text-primary);
}

.sales-volume-subtype-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-bottom: 1px solid var(--border-light);
  padding: 0 16px;
  background: var(--bg-white);
}

.sales-volume-subtype-tab {
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  background: none;
  font-family: inherit;
}

.sales-volume-subtype-tab:hover {
  color: var(--primary-navy-dark, #143a66);
}

.sales-volume-subtype-tab.active {
  color: var(--primary-navy-dark, #143a66);
  border-bottom-color: var(--primary-navy-dark, #143a66);
}

.sales-volume-pane-body {
  padding: 0;
}

.sales-volume-pane-desc {
  padding: 12px 20px 0;
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
}

.sales-volume-table-wrap {
  overflow-x: auto;
}

.sales-volume-settings-table {
  width: 100%;
  border-collapse: collapse;
}

.sales-volume-settings-table th {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--text-tertiary);
  padding: 10px 12px;
  text-align: left;
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-light);
}

.sales-volume-settings-table th:first-child {
  padding-left: 20px;
}

.sales-volume-settings-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-light);
  font-size: 13px;
  vertical-align: middle;
}

.sales-volume-settings-table td:first-child {
  padding-left: 20px;
}

.sales-volume-settings-table tr:last-child td {
  border-bottom: none;
}

.sales-volume-settings-table tr:hover td {
  background: #fafcff;
}

.col-product {
  min-width: 200px;
  max-width: 280px;
}

.col-mkt {
  width: 72px;
}

.sales-volume-asin-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.sales-volume-asin-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}

.sales-volume-asin-code {
  font-size: 11px;
  color: var(--text-tertiary);
  font-family: monospace;
}

.sales-volume-mkt {
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.sales-volume-input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sales-volume-threshold-input {
  width: 72px;
  padding: 5px 8px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  font-size: 12px;
  text-align: center;
  font-family: inherit;
  background: var(--bg-white);
  color: var(--text-primary);
}

.sales-volume-threshold-input:focus {
  outline: none;
  border-color: var(--primary-navy-dark, #143a66);
  box-shadow: 0 0 0 2px rgba(27, 75, 130, 0.1);
}

.sales-volume-threshold-unit {
  font-size: 11px;
  color: var(--text-tertiary);
  white-space: nowrap;
}

.sales-volume-csv-strip {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 20px;
  background: var(--bg-subtle);
  border-top: 1px solid var(--border-light);
  flex-wrap: wrap;
}

.sales-volume-csv-label {
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 180px;
}

.sales-volume-csv-label .icon {
  margin: 0;
  color: var(--text-tertiary);
}

.sales-volume-csv-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sales-volume-btn-csv {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  border: 1.5px solid var(--border-medium);
  background: var(--bg-white);
  color: var(--text-secondary);
}

.sales-volume-btn-csv .icon {
  margin: 0;
}

.sales-volume-btn-csv-dl:hover {
  border-color: var(--primary-navy-dark, #143a66);
  color: var(--primary-navy-dark, #143a66);
}

.sales-volume-btn-csv-ul {
  border-color: var(--primary-navy-dark, #143a66);
  color: var(--primary-navy-dark, #143a66);
  position: relative;
}

.sales-volume-btn-csv-ul:hover {
  background: var(--primary-navy-dark, #143a66);
  color: white;
}

.sales-volume-csv-file {
  display: none;
}

.sales-volume-csv-divider {
  width: 1px;
  height: 16px;
  background: var(--border-medium);
}

.sales-volume-panel-footer {
  padding: 14px 20px;
  background: var(--bg-subtle);
  border-top: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.sales-volume-footer-note {
  font-size: 12px;
  color: var(--text-secondary);
}

.sales-volume-footer-actions {
  display: flex;
  gap: 8px;
}

.sales-volume-btn-cancel {
  padding: 8px 16px;
  background: none;
  border: 1px solid var(--border-medium);
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  color: var(--text-secondary);
}

.sales-volume-btn-cancel:hover {
  border-color: var(--text-secondary);
  color: var(--text-primary);
}

.sales-volume-btn-save {
  padding: 8px 20px;
  background: var(--accent-orange);
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.sales-volume-btn-save:hover:not(:disabled) {
  background: var(--accent-orange-hover);
}

.sales-volume-btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sales-volume-panel-error {
  padding: 8px 20px 12px;
  color: var(--status-critical);
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// SalesVolumeThresholdsPanel.vue?5e65cdbf